/* @flow */
import * as React from "react";
import { withColorsProvider, type ColorSet } from "./ColorsProvider";
import type { StylesObject } from "./utils/prefixStyles";
import prefixStyles from "./utils/prefixStyles";
import GlobalStyleSheet from "./GlobalStyleSheet";
import Tappable from "./Tappable";

const ANIMATION_LENGTH = 250;
const STYLE_ID = "nx-components_CheckboxAnimation";

const stylesheet = `
  @keyframes ${STYLE_ID}_shrink-bounce {
    0% {
      transform: scale(1);
    }
    33% {
      transform: scale(0.85);
    }
    100% {
      transform: scale(1);
    }
  }
  @keyframes ${STYLE_ID}_checkbox-check {
    0%{
      width: 0;
      height: 0;
      border-bottom: 2px solid white;
      transform: translate(-0.3em, 0) rotate(45deg);
    }
    33%{
      width: .2em;
      height: 0;
      border-bottom: 2px solid white;
      border-right: 2px solid white;
      transform: translate(-0.3em, -0.1em) rotate(45deg);
    }
    100%{
      width: .2em;
      height: .5em;
      border-bottom: 2px solid white;
      border-right: 2px solid white;
      transform: translate(-0.3em, -0.6em) rotate(45deg);
    }
  }
  @keyframes ${STYLE_ID}_indeterminate-check {
    0%{
      transform: scale(0);
    }
    100%{
      transform: scale(1);
    }
  }
`;

GlobalStyleSheet.preload(STYLE_ID, stylesheet);

type Props = {
  checked?: ?boolean,
  disabled?: boolean,
  style?: ?StylesObject,
  onTap?: ?() => void,
  colors: ColorSet,
  indeterminate?: ?boolean,
};

class Checkbox extends React.Component<Props> {
  _handleChecked = () => {
    const { onTap } = this.props;

    if (onTap) {
      onTap();
    }
  };

  shouldComponentUpdate(nextProps) {
    return (
      nextProps.checked !== this.props.checked ||
      nextProps.indeterminate !== this.props.indeterminate
    );
  }

  render() {
    const {
      checked,
      style,
      // eslint-disable-next-line no-unused-vars
      onTap,
      colors,
      disabled,
      indeterminate,
      ...domAttrs
    } = this.props;

    const styles = prefixStyles({
      wrapper: {
        position: "relative",
        fontSize: "24px",
        width: "1em",
        height: "1em",
      },
      checkedWrapper: {
        animation: `${STYLE_ID}_shrink-bounce ${ANIMATION_LENGTH}ms ease-out`,
      },
      borderContainer: {
        position: "absolute",
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        borderWidth: "2px",
        borderStyle: "solid",
        borderColor: disabled ? colors.TEXT_DISABLED : "currentColor",
        borderRadius: "2px",
        transition: `all ${ANIMATION_LENGTH}ms ease-out`,
      },
      checkedBorderContainer: {
        borderWidth: "0.5em",
        borderStyle: "solid",
        borderColor: disabled ? colors.TEXT_DISABLED : colors.PRIMARY,
      },
      checkmark: {
        position: "absolute",
        transformOrigin: "bottom left",
        boxSizing: "content-box",
        borderBottom: "0px solid white", // border must be present for Safari to animate
        borderRight: "0px solid white",
        animation: `${STYLE_ID}_checkbox-check ${ANIMATION_LENGTH}ms 125ms ease-out forwards`,
      },
      indeterminateWrapper: {
        position: "absolute",
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      },
      indeterminateCheckMark: {
        width: "0.5em",
        height: "0.25em",
        backgroundColor: "white",
        transform: "scale(0)",
        animation: `${STYLE_ID}_indeterminate-check ${ANIMATION_LENGTH}ms 125ms ease-out forwards`,
        borderRadius: "2px",
      },
    });

    return (
      <Tappable onTap={disabled ? null : this._handleChecked}>
        {({ tapTargetRefHandler }) => (
          <div
            data-component-name="Checkbox"
            data-component-prop-checked={String(checked)}
            data-component-prop-indeterminate={String(indeterminate)}
            style={{
              ...styles.wrapper,
              ...(checked ? styles.checkedWrapper : {}),
              ...style,
            }}
            ref={tapTargetRefHandler}
            tabIndex="0"
            {...domAttrs}
          >
            <div
              style={{
                ...styles.borderContainer,
                ...(checked ? styles.checkedBorderContainer : null),
              }}
            >
              {checked && !indeterminate ? (
                <span
                  data-component-section="checkmark"
                  style={styles.checkmark}
                />
              ) : null}
            </div>
            {checked && indeterminate ? (
              <div style={styles.indeterminateWrapper}>
                <span
                  data-component-section="indeterminate-mark"
                  style={styles.indeterminateCheckMark}
                />
              </div>
            ) : null}
          </div>
        )}
      </Tappable>
    );
  }
}

export default withColorsProvider(Checkbox);



// WEBPACK FOOTER //
// ../nx-components/src/Checkbox.jsx